import React, { useEffect, useState } from "react";
import { Cookie, ShieldCheck, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/context/I18nContext";

const KEY = "pg_cookie_consent";
export const OPEN_COOKIE_PREFS_EVENT = "pg-open-cookie-prefs";

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    if (raw === "all") return { essential: true, preferences: true, analytics: true };
    if (raw === "essential") return { essential: true, preferences: false, analytics: false };
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_COOKIE_PREFS_EVENT));
}

export function CookieConsent() {
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, preferences: false, analytics: false });

  useEffect(() => {
    if (!localStorage.getItem(KEY)) {
      const tm = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(tm);
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      setPrefs(getCookieConsent() || { essential: true, preferences: false, analytics: false });
      setDetail(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_PREFS_EVENT, handler);
    return () => window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, handler);
  }, []);

  const persist = (value) => {
    localStorage.setItem(KEY, JSON.stringify(value));
    localStorage.setItem(`${KEY}_date`, new Date().toISOString());
    setVisible(false);
    setDetail(false);
  };
  const acceptAll = () => persist({ essential: true, preferences: true, analytics: true });
  const refuseAll = () => persist({ essential: true, preferences: false, analytics: false });
  const saveChoices = () => persist({ ...prefs, essential: true });

  if (!visible) return null;

  const T = {
    title: fr ? "Confidentialité et témoins" : "Privacy & cookies",
    text: fr
      ? "Nous utilisons des témoins essentiels au fonctionnement du site et, avec votre consentement, des témoins optionnels, conformément à la Loi 25 (Québec)."
      : "We use cookies essential to the site and, with your consent, optional cookies, in accordance with Law 25 (Quebec).",
    policy: fr ? "Politique de confidentialité" : "Privacy policy",
    refuse: fr ? "Refuser (essentiels)" : "Refuse (essential)",
    customize: fr ? "Personnaliser" : "Customize",
    accept: fr ? "Tout accepter" : "Accept all",
    save: fr ? "Enregistrer mes choix" : "Save my choices",
    essential: fr ? "Témoins essentiels" : "Essential cookies",
    essentialD: fr ? "Nécessaires à la sécurité et à la session. Toujours actifs." : "Required for security and session. Always on.",
    prefsN: fr ? "Préférences" : "Preferences",
    prefsD: fr ? "Mémorisent langue, thème et réglages d'affichage." : "Remember language, theme and display settings.",
    analytics: fr ? "Mesure d'audience" : "Analytics",
    analyticsD: fr ? "Statistiques anonymes d'utilisation pour améliorer le service." : "Anonymous usage statistics to improve the service.",
    always: fr ? "Toujours actif" : "Always on",
  };

  const Row = ({ k, name, desc, locked }) => (
    <div className="flex items-start justify-between gap-4 rounded-md border border-white/10 bg-black/30 p-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-white">{name}</div>
        <div className="text-xs text-white/45 mt-0.5">{desc}</div>
      </div>
      {locked ? (
        <span className="text-[11px] text-[#EAB308] shrink-0 mt-0.5">{T.always}</span>
      ) : (
        <Switch checked={!!prefs[k]} data-testid={`cookie-toggle-${k}`}
          onCheckedChange={(v) => setPrefs((p) => ({ ...p, [k]: v }))}
          className="data-[state=checked]:bg-[#EAB308]" />
      )}
    </div>
  );

  return (
    <div data-testid="cookie-consent"
      className="fixed bottom-4 inset-x-4 z-[60] mx-auto max-w-3xl rounded-xl border border-white/12 bg-[#0a0a0a]/97 backdrop-blur-xl p-5 shadow-2xl">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#EAB308]/12 border border-[#EAB308]/30">
          {detail ? <ShieldCheck className="w-5 h-5 text-[#EAB308]" /> : <Cookie className="w-5 h-5 text-[#EAB308]" />}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white">{T.title}</h3>
            {detail && (
              <button onClick={() => setVisible(false)} className="text-white/40 hover:text-white" data-testid="cookie-close">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="mt-1 text-sm text-white/65 leading-relaxed">
            {T.text} <a href="/confidentialite" className="text-[#EAB308] hover:underline">{T.policy}</a>.
          </p>

          {detail && (
            <div className="mt-4 space-y-2" data-testid="cookie-detail">
              <Row k="essential" name={T.essential} desc={T.essentialD} locked />
              <Row k="preferences" name={T.prefsN} desc={T.prefsD} />
              <Row k="analytics" name={T.analytics} desc={T.analyticsD} />
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2 justify-end">
            <button data-testid="cookie-refuse" onClick={refuseAll}
              className="rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10 transition-colors">
              {T.refuse}
            </button>
            {!detail ? (
              <button data-testid="cookie-customize" onClick={() => setDetail(true)}
                className="rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10 transition-colors">
                {T.customize}
              </button>
            ) : (
              <button data-testid="cookie-save" onClick={saveChoices}
                className="rounded-md border border-[#EAB308]/40 bg-[#EAB308]/10 px-4 py-2 text-sm text-[#EAB308] hover:bg-[#EAB308]/20 transition-colors">
                {T.save}
              </button>
            )}
            <button data-testid="cookie-accept" onClick={acceptAll}
              className="rounded-md bg-[#EAB308] px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition-opacity">
              {T.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
